interface AudioChain {
  input: AudioNode;
  output: AudioNode;
}

interface EffectChain extends AudioChain {
  lowpass: BiquadFilterNode;
  highpass: BiquadFilterNode;
  delayLeft: DelayNode;
  delayRight: DelayNode;
  panLeft: StereoPannerNode;
  panRight: StereoPannerNode;
  delayWet: GainNode;
  delayDry: GainNode;
  delayFeedback: GainNode;
  leftToRightGain: GainNode;
  rightToLeftGain: GainNode;
  leftToRightDampener: BiquadFilterNode;
  rightToLeftDampener: BiquadFilterNode;
  delayMix: GainNode;
  convolver: ConvolverNode;
  reverbWet: GainNode;
  reverbDry: GainNode;
  reverbMix: GainNode;
}

export class AudioGraphService {
  private audioContext: AudioContext | null = null;
  private listeners: Set<(event: string, data: any) => void> = new Set();
  private isInitialized: boolean = false;
  
  // HTML Audio elements and their Web Audio nodes
  private mediaSource1: MediaElementAudioSourceNode | null = null;
  private mediaSource2: MediaElementAudioSourceNode | null = null;
  
  // Audio chains
  private chain1: AudioChain | null = null;
  private chain2: EffectChain | null = null;
  
  // Crossfader and master controls
  private crossfaderGain1: GainNode | null = null;
  private crossfaderGain2: GainNode | null = null;
  private masterGain: GainNode | null = null;
  private crossfaderValue: number = 0.5; // 0 = chain1 only, 1 = chain2 only

  constructor() {
    // Don't initialize AudioContext in constructor for iOS compatibility
  }

  public async initializeAudioContext(): Promise<void> {
    if (this.isInitialized) return;
    
    try {
      this.audioContext = new AudioContext();
      this.setupAudioGraph();
      this.isInitialized = true;
      this.emit('initialized', { sampleRate: this.audioContext.sampleRate });
    } catch (error) {
      console.error('Failed to initialize AudioContext:', error);
      this.emit('error', { message: 'Failed to initialize audio context' });
    }
  }

  private setupAudioGraph(): void {
    if (!this.audioContext) return;

    // Setup crossfader gains
    this.crossfaderGain1 = this.audioContext.createGain();
    this.crossfaderGain2 = this.audioContext.createGain();
    this.masterGain = this.audioContext.createGain();

    // Setup chain 1 (direct with gain reduction)
    const chain1Gain = this.audioContext.createGain();
    chain1Gain.gain.value = 0.5; // Apply 0.5 gain reduction to track side
    chain1Gain.connect(this.crossfaderGain1);
    
    this.chain1 = {
      input: chain1Gain,
      output: this.crossfaderGain1
    };

    // Setup chain 2 (with effects)
    const lowpass = this.audioContext.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 12000;
    lowpass.Q.value = 1;

    const highpass = this.audioContext.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 400;
    highpass.Q.value = 1;

    // Ping pong delay setup
    const delayLeft = this.audioContext.createDelay(5.0);
    delayLeft.delayTime.value = 1;
    
    const delayRight = this.audioContext.createDelay(5.0);
    delayRight.delayTime.value = 1;
    
    // Stereo panning nodes for ping pong effect
    const panLeft = this.audioContext.createStereoPanner();
    panLeft.pan.value = -1; // Full left
    
    const panRight = this.audioContext.createStereoPanner();
    panRight.pan.value = 1; // Full right

    const delayWet = this.audioContext.createGain();
    delayWet.gain.value = 0.3;

    const delayDry = this.audioContext.createGain();
    delayDry.gain.value = 0.7;

    const delayFeedback = this.audioContext.createGain();
    delayFeedback.gain.value = 0.4;

    const delayMix = this.audioContext.createGain();
    delayMix.gain.value = 1.0;

    // Reverb setup with dry/wet mixing
    const convolver = this.audioContext.createConvolver();
    
    const reverbWet = this.audioContext.createGain();
    reverbWet.gain.value = 0.3;

    const reverbDry = this.audioContext.createGain();
    reverbDry.gain.value = 0.7;

    const reverbMix = this.audioContext.createGain();
    reverbMix.gain.value = 1.0;
    
    // Connect effect chain: 
    // lowpass -> highpass -> [ping pong delay dry/wet mix] -> [reverb dry/wet mix] -> crossfader
    lowpass.connect(highpass);
    
    // Ping pong delay routing
    highpass.connect(delayDry); // dry path
    
    // Initial signal goes to left delay first
    highpass.connect(delayLeft);
    
    // Create separate feedback gains for ping pong
    const leftToRightGain = this.audioContext.createGain();
    leftToRightGain.gain.value = 0.4; // Individual feedback gain
    
    const rightToLeftGain = this.audioContext.createGain();
    rightToLeftGain.gain.value = 0.4; // Individual feedback gain
    
    // Create dampener filters for feedback loops
    const leftToRightDampener = this.audioContext.createBiquadFilter();
    leftToRightDampener.type = 'lowpass';
    leftToRightDampener.frequency.value = 8000; // Default dampener frequency
    leftToRightDampener.Q.value = 1;
    
    const rightToLeftDampener = this.audioContext.createBiquadFilter();
    rightToLeftDampener.type = 'lowpass';
    rightToLeftDampener.frequency.value = 8000; // Default dampener frequency  
    rightToLeftDampener.Q.value = 1;
    
    // Create the ping pong chain with dampeners: left -> right -> left -> right...
    // Left delay output goes to left pan and feeds right delay through dampener
    delayLeft.connect(panLeft);
    delayLeft.connect(leftToRightGain);
    leftToRightGain.connect(leftToRightDampener);
    leftToRightDampener.connect(delayRight);
    
    // Right delay output goes to right pan and feeds back to left delay through dampener
    delayRight.connect(panRight);
    delayRight.connect(rightToLeftGain);
    rightToLeftGain.connect(rightToLeftDampener);
    rightToLeftDampener.connect(delayLeft);
    
    // Mix panned delays to wet signal
    panLeft.connect(delayWet);
    panRight.connect(delayWet);
    
    delayDry.connect(delayMix);
    delayWet.connect(delayMix);
    
    // Reverb routing
    delayMix.connect(reverbDry); // dry path
    delayMix.connect(convolver); // wet path
    convolver.connect(reverbWet);
    reverbDry.connect(reverbMix);
    reverbWet.connect(reverbMix);
    
    // Final output
    reverbMix.connect(this.crossfaderGain2);

    this.chain2 = {
      input: lowpass,
      output: this.crossfaderGain2,
      lowpass,
      highpass,
      delayLeft,
      delayRight,
      panLeft,
      panRight,
      delayWet,
      delayDry,
      delayFeedback,
      leftToRightGain,
      rightToLeftGain,
      leftToRightDampener,
      rightToLeftDampener,
      delayMix,
      convolver,
      reverbWet,
      reverbDry,
      reverbMix
    };

    // Connect crossfader to master gain to destination
    this.crossfaderGain1.connect(this.masterGain);
    this.crossfaderGain2.connect(this.masterGain);
    this.masterGain.connect(this.audioContext.destination);

    // Set initial crossfader position
    this.setCrossfaderPosition(this.crossfaderValue);
    this.setMasterVolume(0.8);

    // Load default impulse response for convolver
    this.loadDefaultImpulseResponse();
  }

  private async loadDefaultImpulseResponse(): Promise<void> {
    if (!this.audioContext || !this.chain2) return;

    // Create a simple artificial reverb impulse response
    const length = this.audioContext.sampleRate * 4; // 2 seconds
    const impulse = this.audioContext.createBuffer(2, length, this.audioContext.sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const channelData = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        const decay = Math.pow(1 - i / length, 2);
        channelData[i] = (Math.random() * 2 - 1) * decay * 0.5;
      }
    }
    
    this.chain2.convolver.buffer = impulse;
  }

  // Connect HTML5 audio element to the Web Audio graph
  connectHTMLAudioElement(audioElement: HTMLAudioElement, chainNumber: 1 | 2): void {
    if (!this.audioContext || !this.chain1 || !this.chain2) return;

    try {
      if (chainNumber === 1) {
        if (this.mediaSource1) {
          this.mediaSource1.disconnect();
        }
        this.mediaSource1 = this.audioContext.createMediaElementSource(audioElement);
        this.mediaSource1.connect(this.chain1.input);
      } else {
        if (this.mediaSource2) {
          this.mediaSource2.disconnect();
        }
        this.mediaSource2 = this.audioContext.createMediaElementSource(audioElement);
        this.mediaSource2.connect(this.chain2.input);
      }
    } catch (error) {
      console.error('Failed to connect HTML audio element:', error);
      this.emit('error', { message: 'Failed to connect audio element', chainNumber });
    }
  }

  setCrossfaderPosition(value: number): void {
    if (!this.crossfaderGain1 || !this.crossfaderGain2) return;
    
    this.crossfaderValue = Math.max(0, Math.min(1, value));
    
    // Equal power crossfading
    const angle = this.crossfaderValue * Math.PI / 2;
    this.crossfaderGain1.gain.value = Math.cos(angle);
    this.crossfaderGain2.gain.value = Math.sin(angle);
    
    this.emit('crossfaderChanged', { value: this.crossfaderValue });
  }

  setMasterVolume(value: number): void {
    if (!this.masterGain) return;
    
    this.masterGain.gain.value = Math.max(0, Math.min(1, value));
    this.emit('masterVolumeChanged', { value: this.masterGain.gain.value });
  }

  // Effect controls for chain 2
  setLowpassFrequency(frequency: number): void {
    if (!this.chain2) return;
    this.chain2.lowpass.frequency.value = Math.max(20, Math.min(20000, frequency));
    this.emit('lowpassChanged', { frequency: this.chain2.lowpass.frequency.value });
  }

  setHighpassFrequency(frequency: number): void {
    if (!this.chain2) return;
    this.chain2.highpass.frequency.value = Math.max(20, Math.min(20000, frequency));
    this.emit('highpassChanged', { frequency: this.chain2.highpass.frequency.value });
  }

  setDelayTime(time: number): void {
    if (!this.chain2) return;
    const clampedTime = Math.max(0, Math.min(5, time));
    // Set both delay lines to the same time for ping pong effect
    this.chain2.delayLeft.delayTime.value = clampedTime;
    this.chain2.delayRight.delayTime.value = clampedTime;
    this.emit('delayTimeChanged', { time: clampedTime });
  }

  setDelayFeedback(feedback: number): void {
    if (!this.chain2) return;
    const clampedFeedback = Math.max(0, Math.min(0.95, feedback));
    // Set both ping pong feedback gains to the same value
    this.chain2.leftToRightGain.gain.value = clampedFeedback;
    this.chain2.rightToLeftGain.gain.value = clampedFeedback;
    this.emit('delayFeedbackChanged', { feedback: clampedFeedback });
  }

  setDelayMix(mix: number): void {
    if (!this.chain2) return;
    // mix: 0 = all dry, 1 = all wet
    this.chain2.delayDry.gain.value = Math.max(0, Math.min(1, 1 - mix));
    this.chain2.delayWet.gain.value = Math.max(0, Math.min(1, mix));
    this.emit('delayMixChanged', { mix });
  }

  setReverbMix(mix: number): void {
    if (!this.chain2) return;
    // mix: 0 = all dry, 1 = all wet
    this.chain2.reverbDry.gain.value = Math.max(0, Math.min(1, 1 - mix));
    this.chain2.reverbWet.gain.value = Math.max(0, Math.min(1, mix));
    this.emit('reverbMixChanged', { mix });
  }

  setDelayDampener(frequency: number): void {
    if (!this.chain2) return;
    const clampedFreq = Math.max(20, Math.min(20000, frequency));
    // Set both dampener filters to the same frequency
    this.chain2.leftToRightDampener.frequency.value = clampedFreq;
    this.chain2.rightToLeftDampener.frequency.value = clampedFreq;
    this.emit('delayDampenerChanged', { frequency: clampedFreq });
  }

  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  isAudioContextInitialized(): boolean {
    return this.isInitialized;
  }

  addEventListener(callback: (event: string, data: any) => void): void {
    this.listeners.add(callback);
  }

  removeEventListener(callback: (event: string, data: any) => void): void {
    this.listeners.delete(callback);
  }

  private emit(event: string, data: any): void {
    this.listeners.forEach(callback => callback(event, data));
  }

  getAudioContextState(): string {
    if (!this.audioContext) return 'not-initialized';
    return this.audioContext.state;
  }

  forceResumeAudioContext(): void {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  dispose(): void {
    if (this.mediaSource1) {
      this.mediaSource1.disconnect();
      this.mediaSource1 = null;
    }
    
    if (this.mediaSource2) {
      this.mediaSource2.disconnect();
      this.mediaSource2 = null;
    }
    
    
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.listeners.clear();
    this.emit('disposed', {});
  }
}